// src/admin/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  WalletIcon, ShoppingBagIcon, PackageIcon, UsersIcon,
  ClockIcon, AlertBoxIcon, MailIcon, PlusCircleIcon,
  TagIcon, SettingsIcon, ArrowRightIcon, SparkleIcon,
  TrendingUpIcon,
} from './DashboardIcons';
const getProductImageUrl = (image) => {
  if (!image || typeof image !== 'string') return '';

  const cleanImage = image.trim();

  try {
    if (/^https?:\/\//i.test(cleanImage)) {
      const url = new URL(cleanImage);

      if (
        ['madadline.com', 'www.madadline.com'].includes(url.hostname) &&
        url.pathname.includes('/images/')
      ) {
        const filename = url.pathname.split('/').pop();

        return filename
          ? `https://madadline.com/api/product-images/${encodeURIComponent(filename)}`
          : '';
      }

      return url.href;
    }

    const filename = cleanImage.split('/').filter(Boolean).pop();

    return filename
      ? `https://madadline.com/api/product-images/${encodeURIComponent(filename)}`
      : '';
  } catch (error) {
    console.error('Erreur URL image :', image, error);
    return '';
  }
};
function Dashboard() {
  const [data, setData] = useState({
    users: 0, products: 0, orders: 0, revenue: 0,
    revenue_30d: 0, pending: 0, orders_today: 0,
    new_users_30d: 0, low_stock: 0, out_stock: 0,
    unread_contacts: 0, active_promos: 0, sales_last_7: [],
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${token}` };

    Promise.all([
      axios.get('https://madadline.com/api/dashboard', { headers }).catch(() => ({ data: {} })),
      axios.get('https://madadline.com/api/orders',    { headers }).catch(() => ({ data: [] })),
    ])
      .then(([dashRes, ordersRes]) => {
        const d = dashRes.data || {};
        const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];

        setData(prev => ({ ...prev, ...d }));
        setRecentOrders(orders.slice(0, 5));

        // Top produits
        const productMap = {};
        orders.forEach(o => {
          (o.items || []).forEach(it => {
            const key = it.product?.id || it.product_id;
            if (!key) return;
            if (!productMap[key]) {
              productMap[key] = {
                id: key,
                nom: it.product?.nom || 'Produit',
                image: it.product?.image || '',
                quantity: 0,
                revenue: 0,
              };
            }
            productMap[key].quantity += it.quantity || 0;
            productMap[key].revenue += (parseFloat(it.price || 0) * (it.quantity || 0));
          });
        });
        setTopProducts(
          Object.values(productMap)
            .sort((a, b) => b.quantity - a.quantity)
            .slice(0, 4)
        );
      })
      .finally(() => setLoading(false));
  }, []);

  /* =========================================================
     HELPERS
  ========================================================= */
  const formatMoney = (n) =>
    Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' DH';

  const statusLabel = (s) => ({
    pending: 'En attente', confirmed: 'Confirmée', prepared: 'Préparée',
    shipped: 'Expédiée', delivered: 'Livrée', cancelled: 'Annulée',
  }[s] || s);

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  /* =========================================================
     GRAPHIQUE SVG animé
  ========================================================= */
  const renderChart = () => {
    const sales = data.sales_last_7 || [];
    if (sales.length === 0) {
      return <div className="dash-chart-empty">Aucune vente sur cette période</div>;
    }

    const max = Math.max(...sales.map(s => s.revenue), 100);
    const width = 100;
    const height = 42;

    const points = sales.map((s, i) => {
      const x = (i / Math.max(sales.length - 1, 1)) * width;
      const y = height - (s.revenue / max) * height;
      return { x, y };
    });

    const linePoints = points.map(p => `${p.x},${p.y}`).join(' ');
    const areaPoints = `0,${height} ${linePoints} ${width},${height}`;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="dash-chart-svg">
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C21E56" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#C21E56" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#C21E56" />
            <stop offset="100%" stopColor="#F472B6" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75].map(p => (
          <line key={p} x1="0" y1={height * p} x2={width} y2={height * p}
                stroke="#EAE6DA" strokeWidth="0.15" strokeDasharray="1,1" />
        ))}

        <polygon points={areaPoints} fill="url(#chartGrad)" />

        <polyline
          points={linePoints}
          fill="none"
          stroke="url(#lineGrad)"
          strokeWidth="1"
          strokeLinejoin="round"
          strokeLinecap="round"
          style={{ filter: 'drop-shadow(0 1px 2px rgba(194,30,86,0.3))' }}
        />

        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="1.6" fill="#fff" />
            <circle cx={p.x} cy={p.y} r="1" fill="#C21E56" />
          </g>
        ))}
      </svg>
    );
  };

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="dash">

      {/* ===== HERO ===== */}
      <header className="dash-hero">
        <div className="dash-hero__bg" />
        <div className="dash-hero__content">
          <div>
            <span className="dash-hero__eyebrow">
              <SparkleIcon size={12} />
              Bonjour, Admin
            </span>
            <h1 className="dash-hero__title">Tableau de bord</h1>
            <p className="dash-hero__date">{today}</p>
          </div>
          <Link to="/admin/parametres" className="dash-hero__settings" title="Paramètres">
            <SettingsIcon size={20} />
          </Link>
        </div>
      </header>

      {/* ===== ALERTES ===== */}
      {(data.pending > 0 || data.out_stock > 0 || data.unread_contacts > 0) && (
        <div className="dash-alerts">
          {data.pending > 0 && (
            <Link to="/admin/commandes" className="dash-alert dash-alert--warn">
              <span className="dash-alert__icon"><ClockIcon size={20} /></span>
              <div className="dash-alert__text">
                <strong>{data.pending}</strong> commande{data.pending > 1 ? 's' : ''} en attente
              </div>
              <span className="dash-alert__cta">
                Traiter <ArrowRightIcon size={14} />
              </span>
            </Link>
          )}
          {data.out_stock > 0 && (
            <Link to="/admin/produits" className="dash-alert dash-alert--error">
              <span className="dash-alert__icon"><AlertBoxIcon size={20} /></span>
              <div className="dash-alert__text">
                <strong>{data.out_stock}</strong> produit{data.out_stock > 1 ? 's' : ''} épuisé{data.out_stock > 1 ? 's' : ''}
              </div>
              <span className="dash-alert__cta">
                Gérer <ArrowRightIcon size={14} />
              </span>
            </Link>
          )}
          {data.unread_contacts > 0 && (
            <Link to="/admin/contacts" className="dash-alert dash-alert--info">
              <span className="dash-alert__icon"><MailIcon size={20} /></span>
              <div className="dash-alert__text">
                <strong>{data.unread_contacts}</strong> message{data.unread_contacts > 1 ? 's' : ''} non lu{data.unread_contacts > 1 ? 's' : ''}
              </div>
              <span className="dash-alert__cta">
                Lire <ArrowRightIcon size={14} />
              </span>
            </Link>
          )}
        </div>
      )}

      {/* ===== KPI HERO CARDS ===== */}
      <section className="dash-kpis">
        <Link to="/admin/commandes" className="dash-kpi dash-kpi--primary">
          <div className="dash-kpi__head">
            <span className="dash-kpi__icon"><WalletIcon size={26} /></span>
            <span className="dash-kpi__trend">CA Total</span>
          </div>
          <div className="dash-kpi__value">{formatMoney(data.revenue)}</div>
          <div className="dash-kpi__label">Chiffre d'affaires</div>
          <span className="dash-kpi__arrow"><ArrowRightIcon size={18} /></span>
        </Link>

        <Link to="/admin/commandes" className="dash-kpi">
          <div className="dash-kpi__head">
            <span className="dash-kpi__icon"><ShoppingBagIcon size={24} /></span>
          </div>
          <div className="dash-kpi__value">{data.orders}</div>
          <div className="dash-kpi__label">Commandes</div>
          <span className="dash-kpi__arrow"><ArrowRightIcon size={18} /></span>
        </Link>

        <Link to="/admin/produits" className="dash-kpi">
          <div className="dash-kpi__head">
            <span className="dash-kpi__icon"><PackageIcon size={24} /></span>
          </div>
          <div className="dash-kpi__value">{data.products}</div>
          <div className="dash-kpi__label">Produits</div>
          <span className="dash-kpi__arrow"><ArrowRightIcon size={18} /></span>
        </Link>

        <Link to="/admin/clients" className="dash-kpi">
          <div className="dash-kpi__head">
            <span className="dash-kpi__icon"><UsersIcon size={24} /></span>
          </div>
          <div className="dash-kpi__value">{data.users}</div>
          <div className="dash-kpi__label">Clientes</div>
          <span className="dash-kpi__arrow"><ArrowRightIcon size={18} /></span>
        </Link>
      </section>

      {/* ===== MINI STATS ===== */}
      <section className="dash-mini">
        <div className="dash-mini__item">
          <TrendingUpIcon size={16} className="dash-mini__icon" />
          <span className="dash-mini__value">{formatMoney(data.revenue_30d)}</span>
          <span className="dash-mini__label">CA · 30j</span>
        </div>
        <div className="dash-mini__item">
          <ShoppingBagIcon size={16} className="dash-mini__icon" />
          <span className="dash-mini__value">{data.orders_today}</span>
          <span className="dash-mini__label">Aujourd'hui</span>
        </div>
        <div className="dash-mini__item">
          <UsersIcon size={16} className="dash-mini__icon" />
          <span className="dash-mini__value">+{data.new_users_30d}</span>
          <span className="dash-mini__label">Nouvelles clientes</span>
        </div>
        <div className="dash-mini__item">
          <TagIcon size={16} className="dash-mini__icon" />
          <span className="dash-mini__value">{data.active_promos}</span>
          <span className="dash-mini__label">Promos actives</span>
        </div>
      </section>

      {/* ===== GRAPHIQUE + ACTIONS ===== */}
      <section className="dash-grid">
        {/* Graphique */}
        <div className="dash-card dash-card--chart">
          <div className="dash-card__head">
            <div>
              <h2 className="dash-card__title">Ventes · 7 derniers jours</h2>
              <p className="dash-card__sub">Chiffre d'affaires quotidien</p>
            </div>
          </div>
          {loading ? (
            <div className="dash-loader"><div className="dash-spinner" /></div>
          ) : (
            <>
              <div className="dash-chart">{renderChart()}</div>
              <div className="dash-chart__labels">
                {(data.sales_last_7 || []).map((s, i) => {
                  const d = new Date(s.date);
                  return (
                    <span key={i}>
                      {d.toLocaleDateString('fr-FR', { weekday: 'short' })}
                    </span>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Actions rapides */}
        <div className="dash-card">
          <div className="dash-card__head">
            <h2 className="dash-card__title">Actions rapides</h2>
          </div>
          <div className="dash-quick">
            <Link to="/admin/produits/nouveau" className="dash-quick__item">
              <span className="dash-quick__icon"><PlusCircleIcon size={22} /></span>
              <span className="dash-quick__label">Nouveau produit</span>
            </Link>
            <Link to="/admin/commandes" className="dash-quick__item">
              <span className="dash-quick__icon"><ShoppingBagIcon size={22} /></span>
              <span className="dash-quick__label">Commandes</span>
            </Link>
            <Link to="/admin/promotions" className="dash-quick__item">
              <span className="dash-quick__icon"><TagIcon size={22} /></span>
              <span className="dash-quick__label">Promotions</span>
            </Link>
            <Link to="/admin/contacts" className="dash-quick__item">
              <span className="dash-quick__icon"><MailIcon size={22} /></span>
              <span className="dash-quick__label">Messages</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TOP PRODUITS ===== */}
      {topProducts.length > 0 && (
        <section className="dash-card">
          <div className="dash-card__head">
            <div>
              <h2 className="dash-card__title">Top produits</h2>
              <p className="dash-card__sub">Les plus vendus</p>
            </div>
          </div>
          <div className="dash-top">
            {topProducts.map((p, i) => {
              const img = getProductImageUrl(p.image);
              return (
                <div key={p.id} className="dash-top__item">
                  <span className={`dash-top__rank dash-top__rank--${i + 1}`}>
                    {i + 1}
                  </span>
                  <img
  src={img}
  alt={p.nom || 'Produit'}
  className="dash-top__img"
  loading="lazy"
  onError={(e) => {
    console.error('Image introuvable :', e.currentTarget.src);
    e.currentTarget.style.display = 'none';
  }}
/>
                  <div className="dash-top__info">
                    <div className="dash-top__name">{p.nom}</div>
                    <div className="dash-top__qty">{p.quantity} vendu{p.quantity > 1 ? 's' : ''}</div>
                  </div>
                  <div className="dash-top__price">{formatMoney(p.revenue)}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ===== DERNIÈRES COMMANDES ===== */}
      <section className="dash-card">
        <div className="dash-card__head">
          <div>
            <h2 className="dash-card__title">Dernières commandes</h2>
          </div>
          <Link to="/admin/commandes" className="dash-card__link">
            Voir toutes →
          </Link>
        </div>

        {loading ? (
          <div className="dash-loader"><div className="dash-spinner" /></div>
        ) : recentOrders.length === 0 ? (
          <div className="dash-empty">Aucune commande pour le moment.</div>
        ) : (
          <div className="dash-orders">
            {recentOrders.map(o => (
              <Link
                key={o.id}
                to={`/admin/commandes/${o.id}`}
                className="dash-order"
              >
                <div className="dash-order__avatar">
                  {(o.user?.name || o.customer_name || 'C').charAt(0).toUpperCase()}
                </div>
                <div className="dash-order__info">
                  <div className="dash-order__name">
                    {o.user?.name || o.customer_name || 'Cliente'}
                  </div>
                  <div className="dash-order__meta">
                    #{o.id} · {o.city || '—'} · {new Date(o.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div className="dash-order__right">
                  <div className="dash-order__total">{formatMoney(o.total)}</div>
                  <span className={`dash-order__badge dash-order__badge--${o.status || 'pending'}`}>
                    {statusLabel(o.status)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}

export default Dashboard;